/**
 * Copy Studio — text generation chat (GPT-5.2).
 *
 * Mirrors the image-studio Chat surface but with a much simpler UX:
 *   - Plain text input + Generar button.
 *   - Attach .txt / .docx / .xlsx documents to feed extra context.
 *   - Each assistant reply has quick-refine chips (shorter, emojis,
 *     sales tone, more formal, another variation) that produce a NEW
 *     reply so the user can compare versions side by side.
 *   - 📋 Copiar / 👁 Ver aplicado (preview modal — Fase 3, placeholder
 *     for now).
 *
 * State model intentionally mirrors `Chat.jsx`: messages live in a flat
 * array sorted by created_at, user turns appear on the right, assistant
 * on the left. The chat record is created lazily on the first send so
 * empty rows never litter the sidebar.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import Sidebar from "../components/Sidebar";
import ConsumptionBadges from "../components/ConsumptionBadges";
import ChatsDrawer from "../components/ChatsDrawer";
import PostPreviewModal from "../components/PostPreviewModal";
import RatingButtons from "../components/RatingButtons";
import BrandVoicePanel from "../components/BrandVoicePanel";
import { useMyRatings } from "../lib/useMyRatings";
import { useAuth } from "../lib/auth";
import { Paperclip, Send, Copy as CopyIcon, Eye, Scissors, Smile, ShoppingCart, GraduationCap, Repeat, X, PanelRightOpen, PanelRightClose, BookOpen, HelpCircle, Pencil, Check } from "lucide-react";

const REFINE_CHIPS = [
    { kind: "shorter", label: "Más corto", Icon: Scissors },
    { kind: "emojis", label: "Más emojis", Icon: Smile },
    { kind: "sales", label: "Tono ventas", Icon: ShoppingCart },
    { kind: "formal", label: "Más formal", Icon: GraduationCap },
    { kind: "variant", label: "Otra variación", Icon: Repeat },
];

const PLATFORMS = ["instagram", "facebook", "linkedin"];

export default function CopyStudio() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    // The "?" debug button reveals the raw prompt sent to GPT. We hide
    // it from regular users (and admins) because it can expose internal
    // wording / prompt patterns we'd rather keep behind the scenes.
    const isSuperAdmin = user?.role === "super_admin";
    const [chatId, setChatId] = useState(id || null);
    const [chat, setChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [idea, setIdea] = useState("");
    const [platform, setPlatform] = useState("instagram");
    const [attachments, setAttachments] = useState([]); // {name, text}[]
    const [parsing, setParsing] = useState(false);
    const [dragDepth, setDragDepth] = useState(0);
    const dragActive = dragDepth > 0;
    const [sending, setSending] = useState(false);
    const [voiceExamples, setVoiceExamples] = useState(0);
    const [drawerOpen, setDrawerOpen] = useState(true);
    // Brand-voice slide-over panel — opens from the status bar so the
    // user can edit the tone guide / examples without leaving the
    // chat. Persists ON/OFF preference in localStorage per-tab.
    const [voiceOpen, setVoiceOpen] = useState(false);
    const [useBrandVoice, setUseBrandVoice] = useState(() => {
        try {
            const v = localStorage.getItem("kc:copy:use-brand-voice");
            return v === null ? true : v === "1";
        } catch {
            return true;
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem("kc:copy:use-brand-voice", useBrandVoice ? "1" : "0");
        } catch {
            /* ignore */
        }
    }, [useBrandVoice]);
    // Preview modal — opened by the eye button on each assistant card.
    // `previewMsg` holds the message we're previewing (text + meta);
    // `previewPlatform` seeds the modal's tab from the message's
    // generation platform when present.
    const [previewMsg, setPreviewMsg] = useState(null);
    // Holds the message whose prompt-debug modal is currently open.
    const [promptMsg, setPromptMsg] = useState(null);
    const scrollerRef = useRef(null);
    const fileInputRef = useRef(null);
    // Hydrate the like/dislike state for every copy message rendered in
    // this chat — one network call per session, shared across rows.
    const myRatings = useMyRatings("copy");

    /* --- listen to "Nuevo chat" requests from the drawer --- */
    useEffect(() => {
        const reset = () => {
            setChatId(null);
            setChat(null);
            setMessages([]);
            setIdea("");
            setAttachments([]);
        };
        window.addEventListener("kc:new-copy-chat", reset);
        return () => window.removeEventListener("kc:new-copy-chat", reset);
    }, []);

    /* --- load chat if id present --- */
    useEffect(() => {
        if (!id) {
            setChat(null);
            setMessages([]);
            return;
        }
        api.get(`/copy/chats/${id}`)
            .then((r) => {
                setChatId(id);
                setChat(r.data.chat);
                setMessages(r.data.messages);
            })
            .catch(() => {
                toast.error("Copy no encontrado");
                navigate("/copy");
            });
    }, [id, navigate]);

    /* --- load voice examples count --- */
    useEffect(() => {
        api.get("/brand-voice")
            .then((r) => {
                const ex = (r.data?.examples || []).filter((e) => e.enabled !== false);
                setVoiceExamples(ex.length);
            })
            .catch(() => {});
    }, []);

    /* --- autoscroll to bottom on new messages --- */
    useEffect(() => {
        if (!scrollerRef.current) return;
        const el = scrollerRef.current;
        requestAnimationFrame(() => {
            el.scrollTop = el.scrollHeight;
        });
    }, [messages.length, sending]);

    /* --- attach handler — read .txt/.docx/.xlsx/.pdf as text ---
     * Shared between the clip button and the drag-and-drop overlay.
     * Files are parsed in the browser (no server upload) and only the
     * extracted text is sent to the backend with each generation. We
     * cap at 20 attached files per message — beyond that the prompt
     * starts to suffer from context dilution.
     */
    const ALLOWED_EXTS = ["txt", "csv", "md", "docx", "xlsx", "pdf"];
    const ingestFiles = async (files) => {
        if (!files || files.length === 0) return;
        const usable = Array.from(files).filter((f) => {
            const ext = (f.name.split(".").pop() || "").toLowerCase();
            return ALLOWED_EXTS.includes(ext);
        });
        const rejected = files.length - usable.length;
        if (rejected > 0) {
            toast.error(
                `${rejected} archivo${rejected === 1 ? "" : "s"} ignorado${rejected === 1 ? "" : "s"} (extensión no soportada)`,
            );
        }
        if (usable.length === 0) return;
        setParsing(true);
        try {
            for (const f of usable) {
                try {
                    const text = await readFileAsText(f);
                    if (!text || !text.trim()) {
                        toast.error(`${f.name} está vacío`);
                        continue;
                    }
                    setAttachments((prev) => {
                        if (prev.length >= 20) {
                            toast.error("Máximo 20 adjuntos");
                            return prev;
                        }
                        // Avoid duplicates by name (filename + first 80 chars
                        // — cheap heuristic, the user can always re-add a
                        // truly different file with a unique name).
                        const key = f.name + "::" + text.slice(0, 80);
                        if (prev.some((a) => a.name + "::" + a.text.slice(0, 80) === key)) {
                            return prev;
                        }
                        return [...prev, { name: f.name, text, size: f.size }];
                    });
                } catch (err) {
                    toast.error(`No se pudo leer ${f.name}`);
                }
            }
        } finally {
            setParsing(false);
        }
    };

    const onAttach = async (e) => {
        await ingestFiles(e.target.files);
        e.target.value = "";
    };

    /* --- Drag & drop documents anywhere in the chat area. Mirrors the
     * image-chat drop overlay — overlay only shows for File drags. */
    const onChatDragEnter = (e) => {
        if (!e.dataTransfer?.types?.includes("Files")) return;
        e.preventDefault();
        setDragDepth((d) => d + 1);
    };
    const onChatDragOver = (e) => {
        if (!e.dataTransfer?.types?.includes("Files")) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
    };
    const onChatDragLeave = (e) => {
        if (!e.dataTransfer?.types?.includes("Files")) return;
        e.preventDefault();
        setDragDepth((d) => Math.max(0, d - 1));
    };
    const onChatDrop = async (e) => {
        if (!e.dataTransfer?.types?.includes("Files")) return;
        e.preventDefault();
        setDragDepth(0);
        await ingestFiles(e.dataTransfer.files);
    };

    const removeAttachment = (idx) =>
        setAttachments((prev) => prev.filter((_, i) => i !== idx));

    /* --- send / refine --- */
    const generate = async (overrideKind = null, refineOf = null) => {
        if (sending) return;
        const text = overrideKind ? "" : idea.trim();
        if (!overrideKind && !text) {
            toast.error("Escribe una idea primero");
            return;
        }
        setSending(true);
        try {
            // Lazy chat creation on first send.
            let activeChatId = chatId;
            if (!activeChatId) {
                const r = await api.post("/copy/chats", {
                    title: text.slice(0, 60) || "Nuevo copy",
                });
                activeChatId = r.data.id;
                setChatId(activeChatId);
                setChat(r.data);
                window.history.replaceState(null, "", `/copy/${activeChatId}`);
                window.dispatchEvent(new CustomEvent("kc:chats-updated"));
            }
            const body = {
                idea: text || (REFINE_CHIPS.find((c) => c.kind === overrideKind)?.label || "ajusta el copy"),
                platform,
                use_brand_voice: useBrandVoice,
            };
            if (overrideKind) {
                body.refine_kind = overrideKind;
                body.refine_of = refineOf || undefined;
            }
            if (attachments.length > 0 && !overrideKind) {
                body.attachments = attachments.map((a) => a.text);
            }
            const r = await api.post(
                `/copy/chats/${activeChatId}/generate`,
                body,
            );
            setMessages((prev) => [
                ...prev,
                r.data.user_message,
                r.data.assistant_message,
            ]);
            if (!overrideKind) {
                setIdea("");
                setAttachments([]);
            }
            window.dispatchEvent(new CustomEvent("kc:credits-updated"));
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    "No se pudo generar el copy. Intenta de nuevo.",
                { duration: 6000 },
            );
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="flex h-screen bg-ink-950 overflow-hidden">
            <Sidebar />
            <main className="flex-1 flex min-w-0 min-h-0">
                <section
                    className="flex-1 flex flex-col min-w-0 min-h-0 relative"
                    onDragEnter={onChatDragEnter}
                    onDragOver={onChatDragOver}
                    onDragLeave={onChatDragLeave}
                    onDrop={onChatDrop}
                    data-testid="copy-dropzone"
                >
                    {dragActive && (
                        <div
                            data-testid="copy-drop-overlay"
                            className="absolute inset-0 z-40 pointer-events-none flex items-center justify-center bg-ink-950/70 backdrop-blur-sm"
                        >
                            <div className="rounded-2xl border-2 border-dashed border-periwinkle-400/70 px-10 py-8 text-center">
                                <Paperclip className="w-8 h-8 text-periwinkle-300 mx-auto mb-2" />
                                <p className="text-fog-100 text-sm font-display">
                                    Suelta tus documentos
                                </p>
                                <p className="text-fog-400 text-xs mt-1">
                                    .txt, .docx, .xlsx, .csv, .md, .pdf
                                </p>
                            </div>
                        </div>
                    )}
                {/* header */}
                <header className="px-6 py-4 border-b hairline border-ink-600 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-periwinkle-500/10 flex items-center justify-center text-periwinkle-400">
                            ✦
                        </div>
                        <div>
                            <h1 className="text-base font-display tracking-tight text-fog-100">
                                Copy Studio · Chat
                            </h1>
                            {chat?.title && (
                                <p className="text-[10px] font-mono text-fog-500 tracking-wider uppercase">
                                    {chat.title}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <ConsumptionBadges />
                        <button
                            data-testid="copy-drawer-toggle"
                            onClick={() => setDrawerOpen((v) => !v)}
                            className="w-9 h-9 rounded-lg border hairline border-ink-600 text-fog-300 hover:text-periwinkle-400 hover:border-periwinkle-500/50 flex items-center justify-center"
                            title={drawerOpen ? "Ocultar historial" : "Mostrar historial"}
                        >
                            {drawerOpen ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
                        </button>
                    </div>
                </header>

                {/* scrollable thread */}
                <div
                    ref={scrollerRef}
                    className="flex-1 overflow-y-auto px-6 py-8 space-y-6"
                    data-testid="copy-thread"
                >
                    {messages.length === 0 && !sending && (
                        <div className="max-w-2xl mx-auto text-center py-16">
                            <div className="text-5xl mb-4">✍️</div>
                            <h2 className="text-2xl font-display text-fog-100 mb-2">
                                Creemos tu copy
                            </h2>
                            <p className="text-sm text-fog-400">
                                Describe tu idea o adjunta un documento. Lo
                                escribiré con la voz de tu marca.
                            </p>
                        </div>
                    )}
                    {messages.map((m) => (
                        <MessageRow
                            key={m.id}
                            msg={m}
                            chatId={chatId}
                            chatTitle={chat?.title || ""}
                            initialRating={
                                m.role === "assistant"
                                    ? myRatings.ratingFor(m.id)
                                    : null
                            }
                            onRatingChange={(value, fb) =>
                                myRatings.setRating(m.id, value, fb)
                            }
                            onRefine={(kind) => generate(kind, m.id)}
                            onCopy={() => {
                                navigator.clipboard.writeText(m.text || "");
                                toast.success("Copiado");
                            }}
                            onPreview={() => setPreviewMsg(m)}
                            onShowPrompt={isSuperAdmin ? () => setPromptMsg(m) : null}
                            onTextSaved={(newText) => {
                                setMessages((prev) =>
                                    prev.map((mm) =>
                                        mm.id === m.id ? { ...mm, text: newText } : mm,
                                    ),
                                );
                            }}
                        />
                    ))}
                    {sending && (
                        <div className="max-w-2xl">
                            <div className="rounded-2xl border hairline border-ink-600 bg-ink-900/40 px-5 py-4">
                                <div className="flex items-center gap-2 text-fog-400 text-sm">
                                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-periwinkle-500 animate-pulse" />
                                    Escribiendo…
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* composer */}
                <div className="border-t hairline border-ink-600 px-6 py-3 shrink-0">
                    {/* status line */}
                    <div className="flex items-center justify-between mb-2 font-mono text-[10px] uppercase tracking-widest">
                        <div className="flex items-center gap-3 text-fog-500 flex-wrap">
                            <span>CONFIG</span>
                            <span className="text-fog-300">GPT-5.2 · Copy</span>
                            <span>·</span>
                            {/* Tono toggle — switches whether the brand-voice
                              * block is injected into the GPT system prompt
                              * on the next generation. The state is
                              * persisted across reloads. */}
                            <button
                                type="button"
                                role="switch"
                                aria-checked={useBrandVoice}
                                data-testid="copy-toggle-brand-voice"
                                onClick={() => setUseBrandVoice((v) => !v)}
                                title={useBrandVoice ? "Tono de marca activo · click para desactivar" : "Tono de marca desactivado · click para activar"}
                                className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border hairline transition-colors ${
                                    useBrandVoice
                                        ? "border-emerald-500/50 text-emerald-400 bg-emerald-500/5"
                                        : "border-ink-600 text-fog-500"
                                }`}
                            >
                                <span
                                    className={`w-1.5 h-1.5 rounded-full ${
                                        useBrandVoice ? "bg-emerald-400" : "bg-fog-500"
                                    }`}
                                />
                                Tono {useBrandVoice ? "ON" : "OFF"}
                            </button>
                            <button
                                type="button"
                                data-testid="copy-open-voice-panel"
                                onClick={() => setVoiceOpen(true)}
                                title="Editar reglas del copy"
                                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border hairline border-ink-600 text-fog-300 hover:text-emerald-400 hover:border-emerald-500/40"
                            >
                                <BookOpen className="w-3 h-3" />
                                Reglas{useBrandVoice && voiceExamples > 0 ? `: ${voiceExamples}` : ""}
                            </button>
                        </div>
                    </div>

                    {(attachments.length > 0 || parsing) && (
                        <div className="flex flex-wrap gap-2 mb-2">
                            {attachments.map((a, i) => {
                                const ext = (a.name.split(".").pop() || "").toLowerCase();
                                const sizeKb = a.size ? Math.round(a.size / 1024) : null;
                                return (
                                    <span
                                        key={i}
                                        data-testid={`copy-attachment-${i}`}
                                        title={`${a.name} · ${a.text.length} caracteres`}
                                        className="inline-flex items-center gap-2 text-[11px] font-mono px-2.5 py-1.5 rounded-lg border hairline border-periwinkle-500/40 text-fog-100 bg-periwinkle-500/5"
                                    >
                                        <span className="uppercase text-[9px] tracking-widest text-periwinkle-300">
                                            {ext}
                                        </span>
                                        <span className="max-w-[160px] truncate" data-testid={`copy-attachment-name-${i}`}>
                                            {a.name}
                                        </span>
                                        {sizeKb != null && (
                                            <span className="text-fog-500 text-[9px]">
                                                {sizeKb} KB
                                            </span>
                                        )}
                                        <button
                                            onClick={() => removeAttachment(i)}
                                            data-testid={`copy-attachment-remove-${i}`}
                                            className="text-fog-500 hover:text-rose-400"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                );
                            })}
                            {parsing && (
                                <span
                                    data-testid="copy-attachment-parsing"
                                    className="inline-flex items-center gap-2 text-[11px] font-mono px-2.5 py-1.5 rounded-lg border hairline border-ink-600 text-fog-400"
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-periwinkle-400 animate-pulse" />
                                    Leyendo…
                                </span>
                            )}
                        </div>
                    )}

                    <div className="flex gap-2 items-end">
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept=".txt,.docx,.xlsx,.csv,.md,.pdf"
                            onChange={onAttach}
                            className="hidden"
                            data-testid="copy-file-input"
                        />
                        <button
                            data-testid="copy-attach-btn"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={sending}
                            className="h-12 w-12 rounded-xl border hairline border-ink-600 text-fog-400 hover:text-periwinkle-400 hover:border-periwinkle-500/50 transition-colors disabled:opacity-40 flex items-center justify-center shrink-0"
                            title="Adjuntar documento (.txt, .docx, .xlsx, .csv)"
                        >
                            <Paperclip className="w-4 h-4" />
                        </button>
                        <textarea
                            data-testid="copy-idea-input"
                            value={idea}
                            onChange={(e) => setIdea(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    generate();
                                }
                            }}
                            placeholder="Escribe tu idea de contenido… (ej: lanzamiento de la teja translúcida, resaltar ahorro de luz)"
                            disabled={sending}
                            rows={1}
                            className="flex-1 min-h-[48px] max-h-[200px] resize-none px-4 py-3 rounded-xl border hairline border-ink-600 bg-ink-900 text-fog-100 placeholder:text-fog-500 focus:border-periwinkle-500/50 focus:outline-none text-sm"
                        />
                        <button
                            data-testid="copy-generate-btn"
                            onClick={() => generate()}
                            disabled={sending || !idea.trim()}
                            className="h-12 px-5 rounded-xl bg-periwinkle-500 hover:bg-periwinkle-400 text-ink-950 font-medium disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 shrink-0 transition-all"
                        >
                            <Send className="w-4 h-4" />
                            Generar
                        </button>
                    </div>
                </div>
                </section>
                <ChatsDrawer
                    activeChatId={chatId}
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    basePath="/copy"
                    apiPrefix="/copy/chats"
                    resetEvent="kc:new-copy-chat"
                    allowDeleteAlways
                />
            </main>
            <PostPreviewModal
                open={!!previewMsg}
                onClose={() => setPreviewMsg(null)}
                message={previewMsg}
                initialPlatform={previewMsg?.platform || "instagram"}
            />
            {/* Prompt-debug modal — opened from the "?" icon on each
              * assistant card. Shows the system prompt + the user
              * message exactly as they were sent to GPT, so the user
              * can audit why the output looks the way it does. */}
            {promptMsg && (
                <PromptDebugModal
                    msgId={promptMsg.id}
                    onClose={() => setPromptMsg(null)}
                />
            )}
            {/* Brand-voice slide-over — opens from the status bar.
              * Renders the same editor as the standalone page, but
              * embedded so the user never leaves the chat. */}
            {voiceOpen && (
                <div
                    data-testid="copy-voice-overlay"
                    className="fixed inset-0 z-[60] flex"
                    onMouseDown={(e) => {
                        if (e.target === e.currentTarget) setVoiceOpen(false);
                    }}
                >
                    <div className="flex-1 bg-ink-950/70 backdrop-blur-sm" />
                    <aside
                        className="relative w-full max-w-6xl h-full bg-ink-950 border-l hairline border-ink-700 shadow-2xl flex flex-col"
                        data-testid="copy-voice-panel"
                    >
                        <header className="px-6 py-4 border-b hairline border-ink-700 flex items-center justify-between shrink-0">
                            <div className="flex items-center gap-3">
                                <BookOpen className="w-5 h-5 text-emerald-400" />
                                <div>
                                    <h2 className="text-base font-display text-fog-100">
                                        Reglas del copy
                                    </h2>
                                    <p className="text-[10px] font-mono uppercase tracking-widest text-fog-500 mt-0.5">
                                        Configuración general del prompt
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={useBrandVoice}
                                    data-testid="copy-voice-panel-toggle"
                                    onClick={() => setUseBrandVoice((v) => !v)}
                                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border hairline text-xs font-mono uppercase tracking-widest transition-colors ${
                                        useBrandVoice
                                            ? "border-emerald-500/50 text-emerald-400 bg-emerald-500/10"
                                            : "border-ink-600 text-fog-400"
                                    }`}
                                >
                                    <span
                                        className={`w-1.5 h-1.5 rounded-full ${
                                            useBrandVoice ? "bg-emerald-400 animate-pulse" : "bg-fog-500"
                                        }`}
                                    />
                                    {useBrandVoice ? "Tono activo" : "Tono apagado"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setVoiceOpen(false)}
                                    className="w-8 h-8 rounded-lg text-fog-400 hover:text-fog-100"
                                    aria-label="Cerrar"
                                    data-testid="copy-voice-close"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </header>
                        <div className="flex-1 overflow-y-auto px-6 py-6">
                            <BrandVoicePanel
                                onChanged={({ enabledCount }) =>
                                    setVoiceExamples(enabledCount ?? 0)
                                }
                            />
                        </div>
                    </aside>
                </div>
            )}
        </div>
    );
}

function MessageRow({ msg, onRefine, onCopy, onPreview, onShowPrompt, onTextSaved, chatId, chatTitle, initialRating, onRatingChange }) {
    // Inline edit state for the assistant copy. The textarea grows
    // from the current text; "Guardar" PUTs to the backend; "Cancelar"
    // discards the draft. We keep the draft local so the parent only
    // sees the saved value via onTextSaved.
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(msg.text || "");
    const [savingEdit, setSavingEdit] = useState(false);
    useEffect(() => {
        // Re-sync the draft when the message text changes from outside
        // (e.g. another tab edited it, refine produced a new version)
        // but only when we're not in the middle of editing.
        if (!editing) setDraft(msg.text || "");
    }, [msg.text, editing]);

    const saveEdit = async () => {
        const next = draft.trim();
        if (!next) {
            toast.error("El texto no puede estar vacío");
            return;
        }
        if (next === (msg.text || "").trim()) {
            setEditing(false);
            return;
        }
        setSavingEdit(true);
        try {
            const r = await api.put(`/copy/messages/${msg.id}/text`, { text: next });
            onTextSaved?.(r.data?.text || next);
            toast.success("Copy actualizado");
            setEditing(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo guardar");
        } finally {
            setSavingEdit(false);
        }
    };

    if (msg.role === "user") {
        return (
            <div className="flex justify-end" data-testid={`copy-msg-${msg.id}`}>
                <div className="max-w-2xl flex items-start gap-2">
                    <div className="rounded-2xl rounded-tr-sm bg-ink-800 px-4 py-3 text-sm text-fog-100 whitespace-pre-wrap">
                        {msg.text}
                    </div>
                    <div className="w-7 h-7 rounded-md bg-ink-900 border hairline border-ink-600 flex items-center justify-center text-[10px] font-mono text-fog-400 shrink-0">
                        T
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className="flex justify-start" data-testid={`copy-msg-${msg.id}`}>
            <div className="max-w-2xl flex items-start gap-2 w-full">
                <div className="w-7 h-7 rounded-md bg-periwinkle-500/10 flex items-center justify-center text-periwinkle-400 shrink-0">
                    ✦
                </div>
                <div className="flex-1 rounded-2xl border hairline border-ink-600 bg-ink-900/40 px-5 py-4">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[11px] font-mono uppercase tracking-widest text-fog-500">
                            Propuesta
                            {msg.platform && (
                                <span className="ml-2 text-periwinkle-400">
                                    · {msg.platform}
                                </span>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={onShowPrompt}
                            data-testid={`copy-show-prompt-${msg.id}`}
                            title="Ver el prompt enviado a la IA"
                            className={`w-6 h-6 rounded-full border hairline border-ink-600 text-fog-500 hover:text-periwinkle-400 hover:border-periwinkle-500/50 flex items-center justify-center transition-colors ${onShowPrompt ? "" : "hidden"}`}
                        >
                            <HelpCircle className="w-3.5 h-3.5" />
                        </button>
                    </div>
                    {editing ? (
                        <>
                            <textarea
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                rows={Math.max(4, Math.min(20, (draft.match(/\n/g) || []).length + 3))}
                                data-testid={`copy-edit-textarea-${msg.id}`}
                                autoFocus
                                className="w-full rounded-lg bg-ink-950 border hairline border-periwinkle-500/40 px-4 py-3 text-sm text-fog-100 leading-relaxed focus:border-periwinkle-500 focus:outline-none resize-y"
                            />
                            <div className="flex items-center gap-2 mt-2">
                                <button
                                    onClick={saveEdit}
                                    disabled={savingEdit}
                                    data-testid={`copy-edit-save-${msg.id}`}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-periwinkle-500 hover:bg-periwinkle-400 text-ink-950 text-xs font-medium disabled:opacity-40"
                                >
                                    <Check className="w-3.5 h-3.5" />
                                    {savingEdit ? "Guardando…" : "Guardar"}
                                </button>
                                <button
                                    onClick={() => {
                                        setDraft(msg.text || "");
                                        setEditing(false);
                                    }}
                                    disabled={savingEdit}
                                    data-testid={`copy-edit-cancel-${msg.id}`}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border hairline border-ink-600 text-fog-300 hover:text-fog-100 text-xs"
                                >
                                    <X className="w-3.5 h-3.5" />
                                    Cancelar
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="rounded-lg bg-ink-950/60 px-4 py-3 text-sm text-fog-100 whitespace-pre-wrap leading-relaxed">
                                {msg.text}
                                {msg.edited_at && (
                                    <span className="block mt-2 font-mono text-[9px] uppercase tracking-widest text-fog-500">
                                        Editado
                                    </span>
                                )}
                            </div>
                            <div className="flex flex-wrap gap-2 mt-3">
                                <button
                                    onClick={onPreview}
                                    data-testid={`copy-preview-${msg.id}`}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-ink-950 text-xs font-medium"
                                >
                                    <Eye className="w-3.5 h-3.5" />
                                    Ver aplicado
                                </button>
                                <button
                                    onClick={() => {
                                        setDraft(msg.text || "");
                                        setEditing(true);
                                    }}
                                    data-testid={`copy-edit-${msg.id}`}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border hairline border-ink-600 text-fog-200 hover:text-periwinkle-400 hover:border-periwinkle-500/50 text-xs"
                                >
                                    <Pencil className="w-3.5 h-3.5" />
                                    Editar
                                </button>
                                <button
                                    onClick={onCopy}
                                    data-testid={`copy-copy-${msg.id}`}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border hairline border-ink-600 text-fog-200 hover:text-fog-100 hover:border-periwinkle-500/50 text-xs"
                                >
                                    <CopyIcon className="w-3.5 h-3.5" />
                                    Copiar
                                </button>
                            </div>
                        </>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2">
                        {REFINE_CHIPS.map(({ kind, label, Icon }) => (
                            <button
                                key={kind}
                                onClick={() => onRefine(kind)}
                                data-testid={`copy-refine-${kind}-${msg.id}`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border hairline border-ink-700 text-fog-300 hover:text-periwinkle-400 hover:border-periwinkle-500/50 text-[11px] transition-colors"
                            >
                                <Icon className="w-3 h-3" />
                                {label}
                            </button>
                        ))}
                    </div>
                    {(msg.generation_seconds || msg.cost_usd != null) && (
                        <div className="flex items-center gap-2 mt-3 pt-2 border-t hairline border-ink-700 font-mono text-[9px] uppercase tracking-widest text-fog-500">
                            {msg.generation_seconds && (
                                <span className="text-periwinkle-400">
                                    {msg.generation_seconds.toFixed(1)} s
                                </span>
                            )}
                            {msg.cost_cop != null && (
                                <span>${msg.cost_cop.toFixed(2)} COP</span>
                            )}
                            {(msg.tokens_in != null || msg.tokens_out != null) && (
                                <span className="text-fog-600">
                                    {msg.tokens_in}↑ / {msg.tokens_out}↓ tokens
                                </span>
                            )}
                        </div>
                    )}
                    <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t hairline border-ink-700">
                        <span className="font-mono text-[9px] tracking-widest uppercase text-fog-500">
                            ¿Qué te pareció?
                        </span>
                        <RatingButtons
                            itemType="copy"
                            itemId={msg.id}
                            chatId={chatId}
                            chatTitle={chatTitle}
                            itemPreview={(msg.text || "").slice(0, 240)}
                            initial={initialRating}
                            onChange={onRatingChange}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

/* read .txt / .csv / .md as plain text; .docx / .xlsx are handled by a
 * tiny inline reader using the FileReader API + an external library
 * loaded on-demand. For simplicity, MVP just reads them as text — the
 * backend strips noise. */
async function readFileAsText(file) {
    const ext = file.name.split(".").pop().toLowerCase();
    if (["txt", "csv", "md"].includes(ext)) {
        return await file.text();
    }
    if (ext === "docx") {
        // Tiny inline docx → text fallback: extract text from the XML.
        const arrayBuffer = await file.arrayBuffer();
        // Dynamic import keeps the main bundle slim.
        const JSZip = (await import("jszip")).default;
        const zip = await JSZip.loadAsync(arrayBuffer);
        const xml = await zip.file("word/document.xml").async("string");
        const text = xml.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
        return text;
    }
    if (ext === "xlsx") {
        const arrayBuffer = await file.arrayBuffer();
        const XLSX = await import("xlsx");
        const wb = XLSX.read(arrayBuffer, { type: "array" });
        let out = "";
        for (const name of wb.SheetNames) {
            out += `=== ${name} ===\n`;
            out += XLSX.utils.sheet_to_csv(wb.Sheets[name]);
            out += "\n";
        }
        return out;
    }
    if (ext === "pdf") {
        // Lightweight PDF text extraction via pdfjs-dist. Worker disabled
        // so we don't have to ship the worker file separately. Sufficient
        // for the small briefs / one-pagers that copywriters drop in.
        const arrayBuffer = await file.arrayBuffer();
        const pdfjs = await import("pdfjs-dist/legacy/build/pdf");
        // eslint-disable-next-line no-undef
        if (pdfjs.GlobalWorkerOptions) pdfjs.GlobalWorkerOptions.workerSrc = "";
        const pdf = await pdfjs.getDocument({
            data: arrayBuffer,
            useWorker: false,
            isEvalSupported: false,
        }).promise;
        let out = "";
        for (let p = 1; p <= pdf.numPages; p++) {
            const page = await pdf.getPage(p);
            const content = await page.getTextContent();
            out += content.items.map((it) => it.str).join(" ") + "\n";
        }
        return out.trim();
    }
    // Fallback — read as text and hope for the best.
    return await file.text();
}

/**
 * PromptDebugModal — fetches `/copy/messages/{id}/prompt` and shows the
 * full system prompt + user message that produced this copy. Renders
 * the system prompt with section highlighting (the `[1]`, `[2]`, ...
 * markers from the backend) so the user can see which rules ranked
 * highest. ESC and outside-click close the modal.
 */
function PromptDebugModal({ msgId, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        setLoading(true);
        api.get(`/copy/messages/${msgId}/prompt`)
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [msgId]);
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    const copyAll = () => {
        if (!data) return;
        const block = `SYSTEM PROMPT:\n${data.system_prompt || ""}\n\nUSER MESSAGE:\n${data.user_message || ""}`;
        navigator.clipboard.writeText(block);
        toast.success("Prompt copiado");
    };

    return (
        <div
            data-testid="prompt-debug-overlay"
            className="fixed inset-0 z-[80] flex items-center justify-center bg-ink-950/80 backdrop-blur-sm p-4"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="w-full max-w-3xl max-h-[85vh] rounded-2xl border hairline border-ink-600 bg-ink-900 flex flex-col">
                <header className="px-6 py-4 border-b hairline border-ink-700 flex items-center justify-between shrink-0">
                    <div>
                        <p className="font-mono text-[10px] tracking-[0.25em] text-periwinkle-400 uppercase">
                            Debug
                        </p>
                        <h3 className="font-display text-base text-fog-100 mt-0.5">
                            Prompt enviado a la IA
                        </h3>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={copyAll}
                            data-testid="prompt-debug-copy"
                            className="px-3 py-1.5 rounded-lg border hairline border-ink-600 text-fog-300 hover:text-fog-100 text-xs"
                        >
                            Copiar todo
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            data-testid="prompt-debug-close"
                            className="w-8 h-8 rounded-lg text-fog-400 hover:text-fog-100"
                            aria-label="Cerrar"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </header>
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                    {loading ? (
                        <div className="text-sm text-fog-500 font-mono">Cargando…</div>
                    ) : !data || !data.user_message ? (
                        <div className="text-sm text-fog-400">
                            No hay prompt guardado para este copy. Los copys
                            generados antes de esta versión no tienen
                            historial de prompt. Genera un copy nuevo y vuelve
                            a abrir este botón.
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-wrap gap-2 text-[10px] font-mono uppercase tracking-widest">
                                <Badge label="Modelo" value={data.model_id || "—"} />
                                <Badge label="Plataforma" value={data.platform || "—"} />
                                <Badge
                                    label="Tono marca"
                                    value={data.use_brand_voice ? "ON" : "OFF"}
                                    tone={data.use_brand_voice ? "ok" : "muted"}
                                />
                                {data.tokens_in != null && (
                                    <Badge
                                        label="Tokens"
                                        value={`${data.tokens_in}↑ / ${data.tokens_out}↓`}
                                    />
                                )}
                            </div>
                            <section>
                                <h4 className="font-mono text-[10px] uppercase tracking-widest text-fog-400 mb-2">
                                    Mensaje enviado al modelo
                                </h4>
                                <p className="text-[11px] text-fog-500 mb-2">
                                    Esto es exactamente lo que ve GPT.
                                </p>
                                <PromptBlock text={data.user_message || ""} />
                            </section>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function Badge({ label, value, tone = "neutral" }) {
    const cls = {
        neutral: "border-ink-600 text-fog-300",
        ok: "border-emerald-500/40 text-emerald-400 bg-emerald-500/5",
        muted: "border-ink-700 text-fog-500",
    }[tone];
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border hairline ${cls}`}
        >
            <span className="opacity-60">{label}</span>
            <span className="text-fog-100">{value}</span>
        </span>
    );
}

function PromptBlock({ text }) {
    // Highlight the section markers `[1]`, `[2]`, … with the
    // periwinkle accent so the user can scan the hierarchy at a glance.
    const parts = (text || "").split(/(\[\d+\][^\n]*)/g);
    return (
        <pre
            data-testid="prompt-debug-block"
            className="text-xs text-fog-200 whitespace-pre-wrap leading-relaxed font-mono rounded-lg bg-ink-950 border hairline border-ink-700 px-4 py-3 max-h-[40vh] overflow-y-auto"
        >
            {parts.map((p, i) =>
                /^\[\d+\]/.test(p) ? (
                    <span key={i} className="text-periwinkle-400 font-semibold">
                        {p}
                    </span>
                ) : (
                    <span key={i}>{p}</span>
                ),
            )}
        </pre>
    );
}

